/**
 * Beta Launch Path constants and helpers.
 *
 * This is the single source of truth for the 13-item HeirWise beta journey.
 * The actual ordered items + ref_ids live in the `launch_path_items` table
 * (admin-curated), but the expected shape - exactly 10 lessons + 3 modules,
 * with these specific module keys in this specific order - is enforced here
 * so a regression test can fail loudly if the DB ever drifts.
 *
 * Used by:
 * - useLaunchPath (item locking + completion overlay)
 * - useNextLesson (dashboard "Next Up" hero banner)
 * - LaunchPathCard (Launch Path section + routing)
 * - src/test/launchPath.test.ts (regression)
 */

export const LAUNCH_MODULE_KEYS = [
  "budget_builder",
  "credit_management",
  "scam_defense",
] as const;

export type LaunchModuleKey = (typeof LAUNCH_MODULE_KEYS)[number];

/** Route map for the three beta modules. */
export const MODULE_ROUTES: Record<LaunchModuleKey, string> = {
  budget_builder: "/budget-builder",
  credit_management: "/credit-management",
  scam_defense: "/scam-defense",
};

export function normalizeLaunchModuleKey(refId: string | null | undefined): LaunchModuleKey | null {
  if (!refId) return null;
  const normalized = refId.trim().toLowerCase().replace(/[-\s]+/g, "_").replace(/_module$/, "");
  if (normalized === "budget_builder") return "budget_builder";
  if (normalized === "credit_management") return "credit_management";
  if (normalized === "scam_defense") return "scam_defense";
  return null;
}

/**
 * The official ordered beta launch path. Titles here are the canonical
 * display titles; if the DB row title drifts it should be corrected to match
 * this list. `ref` for lessons is the lesson_name (used by the regression
 * test - UUIDs aren't stable across environments).
 */
export const LAUNCH_PATH: ReadonlyArray<{
  position: number;
  type: "lesson" | "simulator";
  title: string;
  ref: string;
}> = [
  { position: 1, type: "lesson", title: "Needs vs Wants", ref: "Needs vs Wants" },
  { position: 2, type: "lesson", title: "Opportunity Cost", ref: "Opportunity Cost" },
  { position: 3, type: "lesson", title: "Financial Tradeoffs", ref: "Financial Tradeoffs" },
  { position: 4, type: "lesson", title: "Delayed Gratification", ref: "Delayed Gratification" },
  { position: 5, type: "lesson", title: "Cash Flow", ref: "Cash Flow" },
  { position: 6, type: "simulator", title: "Budget Builder Module", ref: "budget_builder" },
  { position: 7, type: "lesson", title: "Credit Score Basics", ref: "Credit Score Basics" },
  { position: 8, type: "lesson", title: "Borrowing Money", ref: "Borrowing Money" },
  { position: 9, type: "lesson", title: "Minimum Payments Trap", ref: "Minimum Payments Trap" },
  { position: 10, type: "simulator", title: "Credit Management Module", ref: "credit_management" },
  { position: 11, type: "lesson", title: "Urgency as a Red Flag", ref: "Urgency as a Red Flag" },
  { position: 12, type: "lesson", title: "Too Good to Be True", ref: "Too Good to Be True" },
  { position: 13, type: "simulator", title: "Scam Defense Module", ref: "scam_defense" },
] as const;

export const LAUNCH_PATH_LENGTH = 13;

/** Module keys that should never appear in the beta launch path. */
export const FORBIDDEN_MODULE_KEYS = [
  "market_simulator",
  "re_investing",
  "vehicle_ownership",
  "homeownership_sim",
  "entrepreneurship_sim",
  "founder_decisions",
  "founder_finance",
  "business_model_sim",
  "industry_competition",
] as const;

/** Lessons that must not appear in the beta launch path. */
export const FORBIDDEN_LESSON_TITLES = ["Income vs Wealth"] as const;

/**
 * Resolve the navigation route for a launch path item.
 * Falls back to `/dashboard` for legacy callers that expect a string.
 */
export function launchItemRoute(
  itemType: "lesson" | "simulator",
  refId: string,
): string {
  return strictLaunchItemRoute(itemType, refId) ?? "/dashboard";
}

/**
 * Strict route resolver for progression controls. Unknown simulator refs must
 * be treated as content/config errors, not as a dashboard fallback.
 */
export function strictLaunchItemRoute(
  itemType: "lesson" | "simulator",
  refId: string,
): string | null {
  if (itemType === "lesson") return `/lesson/${refId}`;
  const moduleKey = normalizeLaunchModuleKey(refId);
  return moduleKey ? MODULE_ROUTES[moduleKey] : null;
}
